import Breadcrumb from '@/components/common/Breadcrumb';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { svgIcons } from '@/components/UI/SvgIcons';
import { Plus } from 'lucide-react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import ErrorMessage from '../../components/common/ErrorMessage';
import {
  GetFilterUrl,
  showToastNotification,
  SpinWheelTypeEnum,
} from '@/utility/helper';
import {
  API_ENDPOINT,
  API_URL_LOYALTY_SERVICE,
  API_URL_PRODUCT_SERVICE,
} from '@/api/constant';
import { callApi } from '@/api/callApi';
import { useEffect, useState } from 'react';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import { PageInfo } from '@/interfaces/common.interface';
import SelectableItem from '@/components/common/SelectAbleItems';
import { useNavigate, useParams } from 'react-router-dom';
import { RoutesLink } from '@/utility/RouteLinks';
import { ISpinResponse } from '@/interfaces/SpinResponse.interface';
import { AddSpinForm } from '@/interfaces/SpinForm.interface';
import { useDebounce } from '@/hooks/useDebounce';

const { CrossIcon } = svgIcons;

interface statesInterface {
  products: IproductResponse[];
  pageInfo: PageInfo;
}
const AddSpinWheel = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const wheelId = id;
  const [searchInput, setSearchInput] = useState('');
  const debounceValue = useDebounce(searchInput);
  const [states, setStates] = useState<statesInterface>({
    products: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  const methods = useForm<AddSpinForm>({
    defaultValues: {
      name: '',
      totalSpins: 1,
      spinItems: [],
    },
  });

  const spinItemsWatch = methods.watch('spinItems');

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  const fetchWheelInfo = async () => {
    try {
      const PATH = `${API_URL_LOYALTY_SERVICE}${API_ENDPOINT.spin}/${wheelId}`;
      const res = await callApi<ISpinResponse>(PATH, { method: 'GET' });
      const updateSpinItem = res.spinItems.map((info) => ({
        ...info,
        spinItemId: info.id || '',
      }));
      methods.reset({ ...res, spinItems: updateSpinItem });
    } catch (error) {
      // console.log('error in fetching info', error);
    }
  };

  const searchProducts = async (name: string) => {
    try {
      const filterQuery = {
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        search: name,
      };
      const query = GetFilterUrl(filterQuery);
      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query ? `${query}` : ''}`;
      const res = await callApi<IproductResponseList>(PATH, { method: 'GET' });
      handleChangeState({
        products: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
      });
    } catch (error) {
      // console.log('error is', error);
    }
  };

  const onSubmit = async (data: AddSpinForm) => {
    try {
      let PATH = `${API_URL_LOYALTY_SERVICE}${API_ENDPOINT.spin}`;
      if (wheelId) {
        PATH = `${PATH}/${wheelId}`;
        await callApi(PATH, { method: 'PATCH', data });
      } else {
        await callApi(PATH, { method: 'POST', data });
      }
      navigate(RoutesLink.spinWheels);
    } catch (error: unknown) {
      const err = error as { message: string };
      showToastNotification('danger', err.message);
    }
  };

  useEffect(() => {
    searchProducts(debounceValue);
  }, [debounceValue]);

  useEffect(() => {
    if (wheelId) fetchWheelInfo();
  }, [wheelId]);

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'spinItems',
  });

  const breadcrumb = [
    { label: 'Spin Wheels', href: '/spin-wheels' },
    { label: 'Add Wheel' },
  ];

  return (
    <Layout>
      <Breadcrumb items={breadcrumb} />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)} className="space-y-6">
          {/* Wheel Details */}
          <Card>
            <h2 className="text-xl font-bold mb-4">Wheel Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <FormField
                  label="Name"
                  placeholder="Enter wheel name..."
                  {...methods.register('name', {
                    required: 'Name is required',
                  })}
                />
                <ErrorMessage
                  message={methods.formState.errors.name?.message}
                />
              </div>
              <div>
                <FormField
                  label="Total Spins"
                  type="number"
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="Total spins in a day"
                  {...methods.register('totalSpins', {
                    valueAsNumber: true,
                    required: 'Total spins is required',
                  })}
                  min={1}
                />
                <ErrorMessage
                  message={methods.formState.errors.totalSpins?.message}
                />
              </div>
            </div>
          </Card>

          {/* Spin Items */}
          <Card>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Spin Items</h2>
              <button
                type="button"
                onClick={() =>
                  append({
                    type: SpinWheelTypeEnum.LOYALTY_POINTS,
                    itemId: null,
                    probability: '',
                    value: '',
                  })
                }
                className="flex items-center gap-2 px-3 py-1 text-blue-600"
              >
                <Plus size={18} />
              </button>
            </div>

            {fields.map((field, index) => {
              const selectedType = spinItemsWatch?.[index]?.type;
              return (
                <div
                  key={field.id}
                  className="relative space-y-2 rounded-md mb-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <FormField
                        label="Type"
                        type="select"
                        placeholder="Item type"
                        options={[
                          {
                            label: 'Loyalty Poins',
                            value: SpinWheelTypeEnum.LOYALTY_POINTS,
                          },
                          {
                            label: 'Product',
                            value: SpinWheelTypeEnum.PRODUCT,
                          },
                          {
                            label: 'Discount',
                            value: SpinWheelTypeEnum.DISCOUNT,
                          },
                          {
                            label: 'Coupon',
                            value: SpinWheelTypeEnum.COUPON,
                          },
                        ]}
                        {...methods.register(`spinItems.${index}.type`, {
                          onChange: (e) => {
                            const newType = e.target.value as SpinWheelTypeEnum;

                            if (
                              newType === SpinWheelTypeEnum.LOYALTY_POINTS ||
                              newType === SpinWheelTypeEnum.DISCOUNT
                            ) {
                              methods.setValue(
                                `spinItems.${index}.itemId`,
                                null,
                              );
                            } else {
                              methods.setValue(`spinItems.${index}.value`, '');
                            }
                          },
                        })}
                      />
                      <ErrorMessage
                        message={
                          methods.formState.errors.spinItems?.[index]?.type
                            ?.message
                        }
                      />
                    </div>
                    <div>
                      <FormField
                        label="Probability"
                        placeholder="e.g. 0.2"
                        {...methods.register(`spinItems.${index}.probability`, {
                          required: 'Probability is required',
                        })}
                      />
                      <ErrorMessage
                        message={
                          methods.formState.errors.spinItems?.[index]
                            ?.probability?.message
                        }
                      />
                    </div>
                  </div>
                  {selectedType === SpinWheelTypeEnum.LOYALTY_POINTS ||
                  selectedType === SpinWheelTypeEnum.DISCOUNT ? (
                    <div>
                      <FormField
                        label="Value"
                        placeholder="Item value"
                        {...methods.register(`spinItems.${index}.value`, {
                          required: 'Value is required',
                        })}
                      />
                      <ErrorMessage
                        message={
                          methods.formState.errors.spinItems?.[index]?.value
                            ?.message
                        }
                      />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <FormField
                        label={
                          selectedType === SpinWheelTypeEnum.PRODUCT
                            ? 'Search Product'
                            : 'Search Coupon'
                        }
                        placeholder={
                          selectedType === SpinWheelTypeEnum.PRODUCT
                            ? 'Search Product'
                            : 'Search Coupon'
                        }
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                      <SelectableItem
                        items={states.products}
                        getId={(item) => item.id}
                        isMutiSelect={false}
                        renderItem={(item) => item.name}
                        fieldName={`spinItems.${index}.itemId`}
                      />
                      <ErrorMessage
                        message={
                          methods.formState.errors.spinItems?.[index]?.itemId
                            ?.message
                        }
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="absolute top-0 right-0 text-red-500 hover:text-red-700"
                  >
                    {CrossIcon}
                  </button>
                </div>
              );
            })}
          </Card>

          {/* Submit Button */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => navigate(-1)}
              type="button"
              className="btn-secondary"
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Save
            </button>
          </div>
        </form>
      </FormProvider>
    </Layout>
  );
};

export default AddSpinWheel;
